import dayjs from "dayjs";
import { toast } from "sonner";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Link } from "react-router-dom";
import { Table } from "@/components/table";
import { MoreVertical } from "lucide-react";
import AddButton from "../button/add-button";
import { useDuplicateProcess } from "@/hooks";
import { useTranslation } from "react-i18next";
import { Process } from "@/types/process.types";
import { Icon } from "@iconify/react/dist/iconify.js";
import ViewProcessFormsModal from "./view-process-forms-modal";
import { AddEditProcessModal } from "./add-edit-process-modal";
import { MoveProcessModal } from "./move-process-modal";

const ProcessTable = ({
  processes,
  status,
  folderId,
}: {
  processes: Process[];
  status?: "ENABLED" | "DISABLED";
  folderId?: string;
}) => {
  const { t } = useTranslation();
  const [selectedProcess, setSelectedProcess] = useState<Process | null>(null);
  const [isEditDialogOpen, setEditDialogOpen] = useState(false);
  const [isAddDialogOpen, setAddDialogOpen] = useState(false);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [selectedProcessForMove, setSelectedProcessForMove] = useState<Process | null>(null);
  const duplicateProcessMutation = useDuplicateProcess();

  const handleEditOpen = (process: Process) => {
    setSelectedProcess(process);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedProcess(null);
  };

  const handleAddOpen = () => {
    setAddDialogOpen(true);
  };

  const handleAddClose = () => {
    setAddDialogOpen(false);
  };

  const [isFormsModalOpen, setFormsModalOpen] = useState(false);
  const [selectedProcessId, setSelectedProcessId] = useState<string | null>(null);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const [selectedProcessForDuplicate, setSelectedProcessForDuplicate] = useState<Process | null>(null);

  const handleFormsOpen = (processId: string) => {
    setSelectedProcessId(processId);
    setFormsModalOpen(true);
  };

  const handleFormsClose = () => {
    setFormsModalOpen(false);
    setSelectedProcessId(null);
  };

  const handleMoveOpen = (process: Process) => {
    setSelectedProcessForMove(process);
    setIsMoveModalOpen(true);
  };

  const handleMoveClose = () => {
    setIsMoveModalOpen(false);
    setSelectedProcessForMove(null);
  };

  const openDuplicateDialog = (process: Process) => {
    setSelectedProcessForDuplicate(process);
    setIsDuplicateDialogOpen(true);
  };

  const closeDuplicateDialog = () => {
    setIsDuplicateDialogOpen(false);
    setSelectedProcessForDuplicate(null);
  };

  const handleDuplicate = async () => {
    if (!selectedProcessForDuplicate) return;
    
    try {
      await duplicateProcessMutation.mutateAsync({
        id: selectedProcessForDuplicate.id,
      });
      closeDuplicateDialog();
      // Toast is handled in the hook
    } catch (error) {
      // Error toast is handled in the hook
    }
  };

  const columns = [
    { key: "name", label: t("processManagement.processName"), width: 200 },
    { key: "groupName", label: t("processManagement.groupName"), width: 150 },
    { key: "type", label: t("processManagement.type"), width: 100 },
    { key: "status", label: t("processManagement.status"), width: 100 },
    { key: "createdBy", label: t("processManagement.createdBy"), width: 150 },
    { key: "updatedAt", label: t("processManagement.updatedAt"), width: 120 },
    { key: "actions", label: t("processManagement.actions"), width: 100 },
  ];

  const tableData = (processes && Array.isArray(processes) ? processes : [])
    .filter((process: Process) => process.status === (status || "ENABLED"))
    .map((process: Process) => ({
      ...process,
      groupName: process.group?.name || "",
      createdBy: `${process.creator?.firstName || ""} ${
        process.creator?.lastName || ""
      }`.trim(),
      updatedAt: process.updatedAt
        ? dayjs(process.updatedAt).format("DD-MMM-YYYY")
        : "",
      actions: (
        <div className="flex items-center space-x-2">
          <button
            type="button"
            className="text-[#494C52]"
            onClick={() => handleEditOpen(process)}
          >
            <Icon icon="ic:baseline-edit" fontSize={18} />
          </button>
          <Link
            to={`/admin/process/process-design/${process.id}`}
            className="text-[#494C52]"
          >
            <Icon icon="mdi:file-document-edit" fontSize={18} />
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="text-[#494C52] focus:outline-none">
                <MoreVertical className="h-5 w-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem
                onClick={() => handleFormsOpen(process.id)}
              >
                <Icon icon="mdi:eye-outline" className="mr-2 h-4 w-4" />
                {t("processManagement.viewForms")}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={duplicateProcessMutation.isPending}
                onClick={() => openDuplicateDialog(process)}
              >
                <Icon icon="mdi:content-copy" className="mr-2 h-4 w-4" />
                {duplicateProcessMutation.isPending
                  ? t("processManagement.duplicating")
                  : t("processManagement.duplicate")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleMoveOpen(process)}>
                <Icon icon="material-symbols:drive-file-move" className="mr-2 h-4 w-4" />
                {t("processManagement.moveToFolder")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    }));

  return (
    <div className="overflow-x-hidden w-full">
      <Table
        paginate
        exportable
        data={tableData}
        columns={columns}
        title={t("sidebar.processNames")}
        exportData={tableData}
        additionalButton={
          <AddButton
            handleClick={handleAddOpen}
            hoverText={t("processManagement.addProcessName")}
          />
        }
      />

      {/* Add Process Modal */}
        <AddEditProcessModal
        mode="add"
        isOpen={isAddDialogOpen}
        onClose={handleAddClose}
        defaultFolderId={folderId}
      />

      {/* Edit Process Modal */}
      {selectedProcess && (
        <AddEditProcessModal
          mode="edit"
          isOpen={isEditDialogOpen}
          onClose={handleEditClose}
          selectedProcess={selectedProcess}
        />
      )}

      {selectedProcessId && (
        <ViewProcessFormsModal
          isOpen={isFormsModalOpen}
          onClose={handleFormsClose}
          processId={selectedProcessId}
        />
      )}

      <AlertDialog open={isDuplicateDialogOpen} onOpenChange={setIsDuplicateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('processManagement.duplicateProcessConfirmation')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('processManagement.duplicateProcessDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={closeDuplicateDialog}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDuplicate}
              disabled={duplicateProcessMutation.isPending}
            >
              {duplicateProcessMutation.isPending ? t('processManagement.duplicating') : t('common.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Move Process Modal */}
      {selectedProcessForMove && (
        <MoveProcessModal
          isOpen={isMoveModalOpen}
          onClose={handleMoveClose}
          processId={selectedProcessForMove.id}
          processName={selectedProcessForMove.name}
          currentFolderId={folderId}
        />
      )}
    </div>
  );
};

export default ProcessTable;
